<script lang="ts">
  import { tasks, risks, resetRisksToSeed, canEdit, addRisk, updateRisk, deleteRisk } from "../lib/stores";
  import { riskLevel, linkedActivitiesFor, PROBABILITY_IMPACT_OPTIONS, RISK_STATUS_OPTIONS } from "../lib/risk";
  import { inputValue, selectValue } from "../lib/dom";
  import type { ProbabilityImpact, RiskStatus } from "../lib/types";

  $: editable = $canEdit;
  $: linkedByCode = Object.fromEntries($risks.map((r) => [r.id, linkedActivitiesFor(r.id, $tasks)]));

  function onProbabilityChange(id: string, value: string) {
    updateRisk(id, { probability: value as ProbabilityImpact });
  }
  function onImpactChange(id: string, value: string) {
    updateRisk(id, { impact: value as ProbabilityImpact });
  }
  function onStatusChange(id: string, value: string) {
    updateRisk(id, { status: value as RiskStatus });
  }
</script>

<div class="tab-panel group">
  <div class="group-head">
    <span class="group-tag risk">Risks</span>
    <span class="group-desc">Seeded only with the risk codes already referenced against activities in the LEAN planning (R001 …) — this is not ABB/PM's risk register. Probability, impact, owner, mitigation and status are blank until someone fills them in; nothing here is guessed.</span>
  </div>

  <section>
    <div class="section-head">
      <div class="section-title">Risk register</div>
      <div class="section-actions">
        {#if editable}
          <button class="btn" type="button" on:click={() => addRisk()}>+ Add risk</button>
          <button class="btn" type="button" on:click={resetRisksToSeed}>Reset to known risk codes</button>
        {/if}
      </div>
    </div>
    <div id="riskTableScroll">
      <table class="acttable risktable">
        <thead>
          <tr>
            <th>Code</th><th style="min-width:190px">Description</th><th>Probability</th><th>Impact</th><th>Level</th>
            <th style="min-width:110px">Owner</th><th style="min-width:190px">Mitigation</th><th style="min-width:110px">Status</th><th style="min-width:120px">Linked activities</th><th></th>
          </tr>
        </thead>
        <tbody>
          {#each $risks as r (r.id)}
            {@const level = riskLevel(r)}
            {@const linked = linkedByCode[r.id] ?? []}
            <tr>
              <td class="mono">
                <input type="text" value={r.id} style="width:72px" disabled={!editable}
                  on:change={(e) => updateRisk(r.id, { id: inputValue(e).trim() || r.id })} />
              </td>
              <td>
                <input type="text" value={r.desc} placeholder="Description" disabled={!editable}
                  on:change={(e) => updateRisk(r.id, { desc: inputValue(e).trim() })} />
              </td>
              <td>
                <select class="probsel" disabled={!editable}
                  on:change={(e) => onProbabilityChange(r.id, selectValue(e))}>
                  {#each PROBABILITY_IMPACT_OPTIONS as opt}
                    <option value={opt} selected={r.probability === opt}>{opt || "— set —"}</option>
                  {/each}
                </select>
              </td>
              <td>
                <select class="impactsel" disabled={!editable}
                  on:change={(e) => onImpactChange(r.id, selectValue(e))}>
                  {#each PROBABILITY_IMPACT_OPTIONS as opt}
                    <option value={opt} selected={r.impact === opt}>{opt || "— set —"}</option>
                  {/each}
                </select>
              </td>
              <td><span class="risk-score" class:low={level === "low"} class:medium={level === "medium"} class:high={level === "high"} class:unset={!level}>{level || "—"}</span></td>
              <td>
                <input type="text" value={r.owner} placeholder="—" disabled={!editable}
                  on:change={(e) => updateRisk(r.id, { owner: inputValue(e).trim() })} />
              </td>
              <td>
                <input type="text" value={r.mitigation} placeholder="—" disabled={!editable}
                  on:change={(e) => updateRisk(r.id, { mitigation: inputValue(e).trim() })} />
              </td>
              <td>
                <select class="statussel" disabled={!editable}
                  on:change={(e) => onStatusChange(r.id, selectValue(e))}>
                  {#each RISK_STATUS_OPTIONS as opt}
                    <option value={opt} selected={r.status === opt}>{opt}</option>
                  {/each}
                </select>
              </td>
              <td class="risk-linked">{linked.length ? linked.join(", ") : "—"}</td>
              <td>
                {#if editable}
                  <button class="rowbtn" title="Remove risk" type="button" on:click={() => deleteRisk(r.id)}>✕</button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="lean-hint">Linked activities are computed automatically from each activity's "Linked risk" text on the Input tab — they update as the register or the activity list changes.</div>
  </section>
</div>
